import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import CartCountrySelect from 'components/pages/cart/CartCountrySelect';
import SelectInput from '@core/components/selectInput/SelectInput';
import Modal from '@core/components/Modal/Modal';
import UseModal from 'hooks/use-modal';
import { MOBILE_SLIDE_UP } from '@core/constants/ModalTypes';

import FilterIcon from 'assets/icons/filter-icon.svg';

import classes from './CompetencyFilters.module.scss';

const seniorityOptions = [
    { value: 'intern', name: 'Intern' },
    { value: 'entry', name: 'Entry' },
    { value: 'mid', name: 'Mid' },
    { value: 'senior', name: 'Senior' },
    { value: 'lead', name: 'Lead' },
    { value: 'executive', name: 'Executive' }
];

const windowOptions = [
    { value: 'last_7d', name: '1W' },
    { value: 'last_30d', name: '1M' },
    { value: 'last_60d', name: '2M' },
    { value: 'last_90d', name: '3M' }
];

const DEFAULT_WINDOW = 'last_30d';

const CompetencyFilters = ({ mobileDevice, onChange }) => {
    const {
        register,
        setValue,
        control,
        watch,
        reset,
        formState: { errors }
    } = useForm({ mode: 'onChange', defaultValues: { window: DEFAULT_WINDOW } });

    const [selectedCountry, setSelectedCountry] = useState({});
    const [countrySelectKey, setCountrySelectKey] = useState(0);
    const { show, showModal, destroyModal } = UseModal();

    const countryCode = selectedCountry?.iso2;
    const seniority = watch('seniority');
    const timeWindow = watch('window');

    useEffect(() => {
        onChange?.({
            countryCode,
            seniority,
            window: timeWindow || DEFAULT_WINDOW
        });
        // onChange is intentionally excluded — parent is expected to memoize if needed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryCode, seniority, timeWindow]);

    const handleReset = () => {
        setSelectedCountry({});
        reset({ country: '', seniority: '', window: DEFAULT_WINDOW });
        setCountrySelectKey((k) => k + 1);
    };

    const activeWindow = timeWindow || DEFAULT_WINDOW;
    const activeFiltersCount =
        (countryCode ? 1 : 0) +
        (seniority ? 1 : 0) +
        (activeWindow !== DEFAULT_WINDOW ? 1 : 0);

    const filterPanel = (
        <div className={classes['filter-wrapper']}>
            <span className={classes['filter-label']}>FILTER</span>
            <div className={classes['filter-item']}>
                <CartCountrySelect
                    key={countrySelectKey}
                    validation={{ required: true }}
                    input={{
                        type: 'text',
                        name: 'country',
                        placeholder: 'Select Country',
                        id: 'country',
                        autoComplete: 'off'
                    }}
                    errors={errors}
                    register={register}
                    selectedCountry={selectedCountry}
                    setSelectedCountry={setSelectedCountry}
                    mobileDevice={mobileDevice}
                    setValue={setValue}
                    horizontal={true}
                />
            </div>
            <div className={classes['filter-item']}>
                <SelectInput
                    input={{
                        name: 'seniority',
                        id: 'seniority',
                    }}
                    placeholder="Seniority"
                    errors={errors}
                    control={control}
                    options={seniorityOptions}
                    optionsNameKey="name"
                    optionsIdKey="value"
                    horizontal={true}
                />
            </div>
            <div className={classes['window-pills']}>
                {windowOptions.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => setValue('window', option.value, { shouldDirty: true })}
                        className={`${classes['window-pill']} ${
                            activeWindow === option.value ? classes['window-pill--active'] : ''
                        }`}
                    >
                        {option.name}
                    </button>
                ))}
            </div>
            <button
                type="button"
                onClick={handleReset}
                className={classes['reset-button']}
            >
                Reset
            </button>
        </div>
    );

    if (!mobileDevice) {
        return filterPanel;
    }

    return (
        <>
            <button
                type="button"
                onClick={showModal}
                className={classes['filter-trigger']}
            >
                <FilterIcon />
                <span>Filter</span>
                {activeFiltersCount > 0 && (
                    <span className={classes['filter-trigger__badge']}>
                        {activeFiltersCount}
                    </span>
                )}
            </button>
            <Modal
                title="Filters"
                type={MOBILE_SLIDE_UP}
                show={show}
                closeHandler={destroyModal}
            >
                {filterPanel}
            </Modal>
        </>
    );
};

export default CompetencyFilters;
